import { Component, h, Prop, State, Event, EventEmitter, Host, Listen } from '@stencil/core';
import { AnchorTarget, SizeType } from '../../types';

@Component({
  tag: 'anchor-card',
  styleUrl: 'card.scss',
  shadow: true,
})
export class Card {
  /**
   * Where should the user go, when clicking on the card.
   */
  @Prop() url = undefined;

  /**
   * Text alignment of the card. Can be either 'center' or 'left'.
   */
  @Prop({ reflect: true }) align: 'center' | 'left' = 'center';

  /**
   * Content alignment of the card. Can be either 'top', 'center' or 'bottom'.
   */
  @Prop({ reflect: true }) verticalAlign: 'top' | 'center' | 'bottom' = 'top';

  /**
   * Whether to show a border around the card.
   */
  @Prop({ reflect: true }) showBorder: boolean = true;

  /**
   * Whether to show an icon in the card.
   */
  @Prop({ reflect: true }) showIcon: boolean = false;

  /**
   * To determine the icon size, defaults to medium.
   */
  @Prop({ mutable: true }) iconSize: SizeType = "medium";

  /**
   * The target for the link.
   */
  @Prop({ mutable: true }) target: AnchorTarget = '_self';

  /**
   * The current state of the card.
   */
  @State() state: 'default' | 'hover' | 'pressed' | 'focus' = 'default';

  /**
   * The variant of the card. The variants "navigation" and "navigation-bold" are in WIP.
   */
  @Prop({ mutable: true }) variant: "default" | "navigation" | "navigation-bold" = "default";

  /**
   * Event emitted when the card is clicked.
   */
  @Event() clickEvent: EventEmitter;

  /**
   * Handles the  enter click of the card.
   */   
  @Listen('keydown')
  handleKeyDown(e: KeyboardEvent) {  
      if (e.key === 'Enter') {
          this.clickEvent.emit(true);
      }
  }

  /**
   * Handles the click event of the card.
   */
  @Listen('click')
  handleClick(_e: PointerEvent) {
    this.clickEvent.emit(true);
  }

  getHeaderSlot() {
    if (this.variant === "default") {
      return <slot name="header" />;
    }
    return <div class={`${this.variant}--header`}><slot name="header" /></div>
  }

  render() {
    return (
      <Host>
        <a
          href={this.url}
          class={`
            anchor-c-card
            anchor-c-card--${this.variant}
            ${this.align} 
            ${this.state} 
            ${this.showBorder ? 'border' : ''}
            ${this.showIcon ? 'icon' : ''}
            anchor-c-card__vertical-${this.verticalAlign}
          `}
          tabindex="0"
          target={this.target}
        >
          <slot name="illustration" />
          <slot name="image" />
          {this.getHeaderSlot()}
          {this.showIcon ? <anchor-icon part="icon" class="anchor-c-card__icon" name="chevron-right" size={this.iconSize} /> : null}
          <slot name="text" />
        </a>
      </Host>
    );
  }
}
